import { v4 as uuidv4 } from 'uuid';
import { currentLocale } from '@/app/i18n';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { sentryService } from '@/services/telemetry/sentry';
import { PubSub } from '@/shared/lib/pub-sub';
import { reactive } from '@/shared/lib/reactive';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { track } from '@/shared/utils/track-telemetry';
import { analytics } from './Analytics';
import { session } from './Session';

export class IdleWatcher extends PubSub {
  USER_ACTIVITY_TRIGGERS = [
    'mousemove',
    'click',
    'keydown',
    'touchstart',
    'touchend',
    'scroll',
  ];

  TICK_INTERVAL = 1000;

  /** Throttle window for user activity events (ms). mousemove fires at 60Hz+;
   *  since tick() runs every 1s, resetting more often than once per second is waste. */
  ACTIVITY_THROTTLE_MS = 1000;

  @reactive
  isIdlePopupOpen = false;

  pauses = [];

  DEFAULT_MAX_IDLE_DURATION = 90000;
  DEFAULT_MAX_IDLE_POPUP_DURATION = 50000;

  // The config is in seconds.
  /** @returns {number} */
  get maxIdleDuration() {
    const seconds = store.kiosk?.max_idle_duration;
    return seconds ? seconds * 1000 : this.DEFAULT_MAX_IDLE_DURATION;
  }

  /** @returns {number} */
  get maxIdlePopupDuration() {
    const seconds = store.kiosk?.max_idle_popup_duration;
    return seconds ? seconds * 1000 : this.DEFAULT_MAX_IDLE_POPUP_DURATION;
  }

  intervalId = null;
  activityCallback = null;
  _routerStepChangedHandler = null;
  _visibilityChangeHandler = null;

  /** Guards against concurrent reset() calls. The async reset flow (session.end,
   *  analytics.flush, navigation) takes multiple seconds. Without this flag,
   *  tick() fires during the await and triggers a second reset(). */
  _isResetting = false;

  /** Timestamp of the last processed activity event, used for throttling. */
  _lastActivityTimestamp = 0;

  /** Wall-clock timestamp (ms) of the last user activity or explicit reset.
   *  tick() compares Date.now() against this to compute actual elapsed idle time,
   *  making the idle counter immune to setInterval throttling (power-saving,
   *  CPU spikes) that would cause tick-counting to drift on a 24/7 kiosk. */
  _lastResetTimestamp = 0;

  /** Wall-clock timestamp (ms) when the idle popup was opened.
   *  Used to compute elapsed popup duration from real time, not tick counts. */
  _popupOpenedTimestamp = 0;

  get isPaused() {
    return this.pauses.length > 0;
  }

  constructor() {
    super();
    exposeToGlobalScope(this, 'IdleWatcher');
  }

  initialize() {
    if (!store.isMiniPCKiosk) {
      sentryService.addBreadcrumb(
        'idle_watcher',
        'Initialization skipped - not MiniPC kiosk',
        {
          is_mini_pc: store.isMiniPCKiosk,
        },
        'debug'
      );
      return;
    }

    try {
      // Full cleanup of previous state to prevent accumulation on re-init.
      // cleanup() now resets all counters, flags, pauses, and popup state,
      // so no redundant resets are needed here.
      this.cleanup();

      // Seed the wall-clock idle baseline so the first tick measures from now
      this._lastResetTimestamp = Date.now();

      // Store handler reference for proper cleanup
      this._routerStepChangedHandler = () => this.resetCountdown();
      router.on(router.EVENTS.STEP_CHANGED, this._routerStepChangedHandler);
      this._registerUserActivityListeners();

      // Pause idle counting when tab is backgrounded. Browser throttles
      // setInterval in hidden tabs (to 1/sec or worse), which causes the
      // idle counter to drift. On return to foreground the accumulated
      // ticks could instantly trigger the idle popup or reset.
      this._visibilityChangeHandler = () => this._onVisibilityChange();
      document.addEventListener(
        'visibilitychange',
        this._visibilityChangeHandler
      );

      this.intervalId = setInterval(() => this.tick(), this.TICK_INTERVAL);

      sentryService.addBreadcrumb('idle_watcher', 'IdleWatcher initialized', {
        max_idle_duration_sec: this.maxIdleDuration / 1000,
        max_popup_duration_sec: this.maxIdlePopupDuration / 1000,
        tick_interval_ms: this.TICK_INTERVAL,
      });
    } catch (error) {
      sentryService.captureException(error, {
        tags: { component: 'IdleWatcher', operation: 'initialize' },
        extra: {
          maxIdleDurationSec: this.maxIdleDuration / 1000,
          maxIdlePopupDurationSec: this.maxIdlePopupDuration / 1000,
        },
      });
      throw error;
    }
  }

  tick() {
    if (this.isPaused || this._isResetting) {
      return;
    }

    const now = Date.now();

    // Use wall-clock elapsed time instead of counting ticks.
    // setInterval can be throttled by the browser (power-saving, CPU spike,
    // background tab edge cases) causing tick-counting to drift significantly
    // on a 24/7 kiosk. Wall-clock comparison is immune to this.
    const idleElapsed = now - this._lastResetTimestamp;

    if (!this.isIdlePopupOpen) {
      // Phase 1: waiting for user to go idle
      if (idleElapsed >= this.maxIdleDuration) {
        this.isIdlePopupOpen = true;
        this._popupOpenedTimestamp = now;

        // Dismiss any open privacy policy popup to prevent two modal
        // Popups from stacking on the kiosk screen. The idle popup takes
        // priority and the privacy policy can be re-opened afterward.
        useUiStore().showPrivacyPolicyPopup = false;

        analytics.trackIncrement(analytics.METRICS.session_idle);

        sentryService.addBreadcrumb(
          'idle_watcher',
          'User went idle - showing popup',
          {
            idle_duration_sec: idleElapsed / 1000,
            max_idle_duration_sec: this.maxIdleDuration / 1000,
            current_step: router.step,
          },
          'warning'
        );
      }
    } else {
      // Phase 2: popup is open, waiting for timeout or user action.
      // Uses `else` so the popup duration does NOT start advancing on the
      // same tick that opened it -- the user gets the full maxIdlePopupDuration.
      const popupElapsed = now - this._popupOpenedTimestamp;
      if (popupElapsed >= this.maxIdlePopupDuration) {
        this.reset();
      }
    }
  }

  /**
   * Register throttled user activity listeners on document.
   * mousemove fires at display refresh rate (60Hz+). Since our tick interval
   * is 1 second, resetting the idle counter more than once per second is
   * pure waste on a resource-constrained kiosk. The throttle wrapper
   * ensures at most one resetCountdown() per ACTIVITY_THROTTLE_MS.
   */
  _registerUserActivityListeners() {
    // Remove old listeners first to prevent accumulation
    this._removeUserActivityListeners();

    // Create a single throttled callback shared by all event types
    this.activityCallback = () => {
      const now = Date.now();
      if (now - this._lastActivityTimestamp >= this.ACTIVITY_THROTTLE_MS) {
        this._lastActivityTimestamp = now;
        this.resetCountdown();
      }
    };

    for (const eventType of this.USER_ACTIVITY_TRIGGERS) {
      // passive: true tells the browser we won't call preventDefault(),
      // allowing scroll/touch events to be handled without waiting for JS
      document.addEventListener(eventType, this.activityCallback, {
        passive: true,
      });
    }
  }

  _removeUserActivityListeners() {
    if (this.activityCallback) {
      for (const eventType of this.USER_ACTIVITY_TRIGGERS) {
        document.removeEventListener(eventType, this.activityCallback);
      }
      this.activityCallback = null;
    }
  }

  resetCountdown() {
    this._lastResetTimestamp = Date.now();
  }

  /**
   * Handle document visibilitychange.
   * When hidden: auto-pause so browser timer throttling doesn't cause
   * idle counter drift that triggers spurious resets on return.
   * When visible: auto-resume the pause we added.
   */
  _onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      // Only add a visibility pause if we don't already have one
      if (!this._visibilityPause) {
        this._visibilityPause = this.pause();
        track('[IdleWatcher] Sentry call failed:', () => {
          const elapsedMs = this._lastResetTimestamp
            ? Date.now() - this._lastResetTimestamp
            : 0;
          sentryService.addBreadcrumb(
            'idle_watcher',
            'Paused - tab hidden',
            { idle_duration_sec: elapsedMs / 1000 },
            'debug'
          );
        });
      }
    } else {
      // Tab is visible again - resume
      if (this._visibilityPause) {
        this._visibilityPause.continue();
        this._visibilityPause = null;
        // Reset idle counter on return - the user is actively looking at the screen
        this.resetCountdown();
        sentryService.addBreadcrumb(
          'idle_watcher',
          'Resumed - tab visible',
          {},
          'debug'
        );
      }
    }
  }

  pause() {
    const uuid = uuidv4();
    let continued = false;
    const pause = {
      uuid,
      continue: () => {
        // Idempotent: calling continue() twice is a no-op.
        // This prevents double-removal when _clearAllPauses() has already
        // drained the array and the external caller later calls continue().
        if (continued) {
          return;
        }
        continued = true;
        this.pauses = this.pauses.filter((p) => p.uuid !== uuid);
        sentryService.addBreadcrumb(
          'idle_watcher',
          'Idle watching resumed',
          {
            pause_uuid: uuid.slice(0, 8),
            remaining_pauses: this.pauses.length,
          },
          'debug'
        );
      },
    };

    this.pauses.push(pause);

    track('[IdleWatcher] Sentry call failed:', () => {
      const elapsedMs = this._lastResetTimestamp
        ? Date.now() - this._lastResetTimestamp
        : 0;
      sentryService.addBreadcrumb(
        'idle_watcher',
        'Idle watching paused',
        {
          pause_uuid: uuid.slice(0, 8),
          total_pauses: this.pauses.length,
          idle_elapsed_sec: elapsedMs / 1000,
        },
        'debug'
      );
    });

    return pause;
  }

  closeIdlePopup() {
    const now = Date.now();
    const popupDurationMs = this._popupOpenedTimestamp
      ? now - this._popupOpenedTimestamp
      : 0;

    sentryService.addBreadcrumb(
      'idle_watcher',
      'Idle popup dismissed by user',
      {
        popup_duration_sec: popupDurationMs / 1000,
        max_popup_duration_sec: this.maxIdlePopupDuration / 1000,
        outcome: 'user_dismissed',
      }
    );

    this.isIdlePopupOpen = false;
    this._lastResetTimestamp = now;
    this._popupOpenedTimestamp = 0;
  }

  /**
   * Properly continue() each pause in the array so that externally-held
   * pause handles become idempotent no-ops. Then clear the array.
   * This prevents orphan pauses that keep isPaused === true forever.
   */
  _clearAllPauses() {
    // Snapshot and clear first to avoid re-entrant filter operations
    // from individual continue() calls mutating the array mid-iteration.
    const snapshot = this.pauses;
    this.pauses = [];
    for (const pause of snapshot) {
      try {
        pause.continue();
      } catch (_e) {
        // Individual continue() failure must not prevent clearing others
      }
    }
  }

  cleanup() {
    const hadActiveInterval = Boolean(this.intervalId);
    const wasPopupOpen = this.isIdlePopupOpen;
    const activePauses = this.pauses.length;

    // Clean up interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Clean up user activity event listeners
    this._removeUserActivityListeners();

    // Remove router event listener
    if (this._routerStepChangedHandler) {
      router.off(router.EVENTS.STEP_CHANGED, this._routerStepChangedHandler);
      this._routerStepChangedHandler = null;
    }

    // Remove visibilitychange listener
    if (this._visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this._visibilityChangeHandler
      );
      this._visibilityChangeHandler = null;
    }

    // Properly continue() the visibility pause BEFORE nulling the reference.
    // Just nulling _visibilityPause leaves its entry orphaned in the pauses
    // array -- permanently keeping isPaused === true.
    if (this._visibilityPause) {
      this._visibilityPause.continue();
      this._visibilityPause = null;
    }

    // Drain all pauses properly so external callers' handles become no-ops
    this._clearAllPauses();

    // Reset all counters and flags to prevent stale state on re-init.
    // These must reset in cleanup() too (not only initialize()), so a
    // standalone cleanup (App.vue beforeUnmount) doesn't leave stale counters.
    this.isIdlePopupOpen = false;
    this._isResetting = false;
    this._lastActivityTimestamp = 0;
    this._lastResetTimestamp = 0;
    this._popupOpenedTimestamp = 0;

    sentryService.addBreadcrumb(
      'idle_watcher',
      'IdleWatcher cleaned up',
      {
        had_active_interval: hadActiveInterval,
        was_popup_open: wasPopupOpen,
        active_pauses: activePauses,
      },
      'debug'
    );
  }

  async reset() {
    // Re-entrancy guard: prevent concurrent resets.
    // tick() keeps firing during the async session.end() / flush() calls below.
    // Without this guard, a second tick while the first reset is still awaiting
    // would trigger a second concurrent reset => double session.end(), double
    // navigation, race conditions in store cleanup.
    if (this._isResetting) {
      return;
    }
    this._isResetting = true;

    try {
      await this._performReset();
    } finally {
      this._isResetting = false;
    }
  }

  /** Internal reset implementation, called only from reset() behind the guard. */
  async _performReset() {
    const now = Date.now();

    // Capture metrics BEFORE clearing state (for Sentry reporting below).
    const popupDurationMs = this._popupOpenedTimestamp
      ? now - this._popupOpenedTimestamp
      : 0;
    const idleDurationMs = this._lastResetTimestamp
      ? now - this._lastResetTimestamp
      : 0;
    const currentStep = router.step;

    // CRITICAL: Clear local state BEFORE any async/Sentry work so that
    // tick() cannot re-trigger, even if sentryService.withSpan throws.
    // Doing this cleanup inside the withSpan callback would let a Sentry
    // SDK failure leave stale popup/pause state, causing the idle watcher
    // to immediately re-fire on the next tick.
    this._clearAllPauses();
    this._visibilityPause = null;
    this._lastResetTimestamp = now;
    this._popupOpenedTimestamp = 0;
    this.isIdlePopupOpen = false;

    // The actual session teardown goes through withSpan for tracing.
    // If Sentry is broken, the try/catch ensures we still navigate home.
    try {
      await sentryService.withSpan(
        'idle_watcher.reset',
        'idle.lifecycle',
        async () => {
          // Track idle/popup duration metrics
          if (popupDurationMs > 0) {
            sentryService.trackMetric(
              'idle_watcher.popup_duration_before_reset',
              popupDurationMs / 1000,
              'second'
            );
          }
          if (idleDurationMs > 0) {
            sentryService.trackMetric(
              'idle_watcher.idle_duration_before_reset',
              idleDurationMs / 1000,
              'second'
            );
          }

          sentryService.addBreadcrumb(
            'idle_watcher',
            'Session reset due to idle timeout',
            {
              popup_duration_sec: popupDurationMs / 1000,
              idle_duration_sec: idleDurationMs / 1000,
              max_popup_duration_sec: this.maxIdlePopupDuration / 1000,
              previous_step: currentStep,
              // Full session ID is intentional Sentry metadata for DB joins.
              session_id: store.sessionId,
              outcome: 'session_reset',
            },
            'warning'
          );

          // Track idle_reset BEFORE session.end() to ensure it is attributed to
          // the current session. If tracked after end(), the session ID is null and
          // the event bleeds into the next session's identity.
          analytics.trackBoolean(analytics.METRICS.session_idle_reset);

          // session.end() calls analytics.flushForSession() internally to drain
          // the queue with the ending session's ID, then resets analytics state.
          // IMPORTANT: session.end() also calls resetForNewSession()
          // which resets the internal locale to the kiosk default (e.g., Dutch).
          await session.end('idle_timeout');
          await sentryService.flush(1000);

          if (router.isStepPage) {
            // Pass the reset locale explicitly to avoid URL/state desync.
            // Without this, redirectToStep('') uses the URL's stale locale
            // (e.g., en-GB from the previous session) while internal state
            // has just reset to the kiosk default (e.g., Dutch). The URL
            // then shows en-GB while the UI shows Dutch, and a click on
            // English looks like a duplicate navigation to Vue Router (URL
            // already en-GB) so nothing happens.
            router.redirectToStep('', {
              localeCode: currentLocale.value?.code,
            });
          }
        }
      );
    } catch (error) {
      sentryService.captureException(error, {
        tags: { component: 'IdleWatcher', operation: 'reset' },
        extra: {
          previous_step: currentStep,
          popup_duration_sec: popupDurationMs / 1000,
          idle_duration_sec: idleDurationMs / 1000,
        },
      });
      // Still attempt navigation even if session end or Sentry fails.
      // This catch also covers withSpan itself throwing (e.g., Sentry
      // SDK not loaded) -- otherwise the kiosk gets stuck.
      if (router.isStepPage) {
        // Use reset locale to keep URL in sync with internal state
        router.redirectToStep('', {
          localeCode: currentLocale.value?.code,
        });
      }
    }
  }
}

export const idleWatcher = new IdleWatcher();
